/* eslint-disable max-lines */

// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import Error from '../../../common/nodejs/errors';
import handler from '../../../share/util/apiResultValidator';
import session from '../../../share/session';
import logWrapper from '../../../share/logWrapper';
import api from '../../../common/api';
import alert from '../../../share/alert';
import config from '../../../share/config';
import cstmDialog from './CustomDialog';
import eventLogUtil from '../../../share/util/eventLogUtil';
import reprogram from '../../../share/util/reprogram';
import util from '../../../share/util/utils';
import loading from '../../../share/components/vue/ProcessingIcon';
import resource from '../../../share/util/resourceMap';
import authority from '../../../share/util/authorityCheck';
import vehicle from '../../../share/util/vehicle';
const publishedUnitListTestMode = require('../../../data/testMode/publishedUnits.json');
const INIT = 'init';
const COMP = 'comp';
const CURRENT = 'current';

let modalQueue = [];

export default {
  data() {
    return {
      showModalQueueWarning: false,
      processing: false,
      containerMode: '',
      currentProgramStatus: '',
      previousProgramStatus: '',
      attentionIgOn: false,
      executingIgOn: false,
      executingIgOff: false,
      completionCcuReprogramming: false,
      execStatus: '',
      execPhase: '',
      attentionContents: '',
      countDownTimer: 0,
      progressObjects: {},
      /** HTML 非バインド変数 */
      containerOrder: '',
      watcher: null,
      igStateTimer: null,
      executeRspMsgCheckTimeout: Number(process.env.VUE_APP_EXECUTE_RSP_CHECK_INTERVAL),
      testReproUnitId: null,
      modalQueueMsg: { lead: '', tail: '' },
      canExitAllWatcher: false,
      fileType: '',
      isReproCompleted: false,
      isAlreadyShowCCUContainer: false,
      innerProgressIcon: false,
    };
  },

  components: {
    'cstm-dialog': cstmDialog,
    'processing-view': loading,
  },

  computed: {
    ...mapState({
      systems: state => state.reprogram.systems,
      download: state => state.reprogram.download,
      preProgram: state => state.reprogram.preProgram,
      postProgram: state => state.reprogram.postProgram,
      reuseBinMot: state => state.reprogram.is_bin_mot_reuse,
      containerHeight(state) { return state.app.clientHeight - 50; },
      processMode: state => state.app.viewProcessMode,
      // 車種情報取得の接続状況
      vehicleStatus: state => state.vehicle.status,
      vehicleError: state => state.vehicle.error,
      vehicleWatcher: state => state.vehicle.watcher,
      progress: state => state.vehicle.progress,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      warnings: state => state.vehicle.warnings,
      targetUnitList: state => state.masterResources.targetUnits,
      netAppId: state => state.netAppConnector.pairingAppId,
      isTestMode: state => state.netAppConnector.isTestMode,
      unitInfo: state => state.targetUnit.unitInfo,
      isLongInterval: state => state.targetUnit.isLongInterval,
      language: state => state.systems.language,
      idsInfo: state => state.cache.systemIdsInfo,
      ccuReproInfo: state => state.cache.ccuReproInfo,
      authGroup: state => state.userProperty.group,
      isTechnicalHq: state => state.userProperty.userInfo.isTechnicalHq,
      serialNumber: state => state.cache.serialNumber,
    })
  },
  // router params 対応
  props: ["response"],

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('reprogram', ['setProgress', 'setPFCReuseStatus', 'setResultStatus']),
    ...mapActions('netAppConnector', ['getPairingStatus']),
    ...mapActions('vehicle', ['updateVehicleInfo', 'setWarningQueue', 'setStackedWarningQueue']),
    ...mapActions('cache', ['cacheVehicleInfo']),
    ...mapActions('userProperty', ['setUserCache']),
    ...mapActions('targetUnit', ['setUnitDetailInfo']),
    ...mapActions('targetUnit', ['setIsLongInterval']),
    ...mapActions('table', ['setNativeSystemTable']),

    // システム情報画面に戻る
    cancel: function () {
      this.$router.push({ name: 'systemInformation' });
    },

    // Get target unit display name
    getTargetUnitDisplayName: function (systemName) {
      const systemNameList = systemName.split('/');
      const displayNames = [];
      for (let sysIndex = 0; sysIndex < systemNameList.length; ++sysIndex) {
        const filteredList = this.targetUnitList.filter(item => item.name === systemNameList[sysIndex]);
        if (filteredList.length > 0) {
          const systemInfo = filteredList[0];
          for (let index = 0; index < systemInfo.display_names.length; ++index) {
            if (systemInfo.display_names[index].language === this.language) {
              displayNames.push(systemInfo.display_names[index].name);
              break;
            }
          }
        }
      }
      return displayNames.length === 0 ? '' : displayNames.join('/');
    },

    // リプロ実行
    execute: async function () {
      this.$emit('setModalQueueAvailable', true);
      this.setUserCache({ key: config.USER_CACHE_REPRO_TARGET_INFO, value: this.vehicleInfo });
      let startResponse = null;

      try {
        // if (this.reuseBinMot) await api.refreshToken();
        this.processing = true;
        this.innerProgressIcon = true;
        // リプロ開始のリクエスト
        const loginToken = session.getLoginToken();
        const token = session.getToken();
        if (this.isTestMode) {
          const endpoint = '/test/' + this.netAppId + '/startRepro';
          startResponse = await api.postCall(config.REPRO, endpoint, {
            is_req_resend: false,
            id_token: (this.reuseBinMot) ? token.id_token : loginToken.id_token,
            unit_id: String(this.getUnitId())
          });
        } else {
          await eventLogUtil.createEventLogHandleId(this.netAppId, config.REPRO);
          const endpoint = '/netApps/' + this.netAppId + '/startRepro';
          startResponse = await api.postCall(config.REPRO, endpoint, {
            is_req_resend: false,
            is_bin_mot_reuse: this.reuseBinMot, // 初回実行
            id_token: (this.reuseBinMot) ? token.id_token : loginToken.id_token,
            pfc_id: this.unitInfo.fileId,
            unit_id: this.unitInfo.unitId,
            serial_number: this.serialNumber,
          });
        }
        this.processing = false;

        handler.validate(handler.validateTypes.all, startResponse, this, null,
          () => {
            // システムごとに進捗状況をセット
            this.setProgress({
              systems: startResponse.data.repro_status,
              download: { progress: 0, total_bytes: 0, downloaded_bytes: 0, remaining_download_time: 0, total_download_time: 0 },
              preProgram: { exec_status: 'none', progress: 0 },
              postProgram: { exec_status: 'none', progress: 0 }
            });
            // リプロ実行中コンテナを表示
            this.previousProgramStatus = config.REPRO_FILE_DOWNLOADING;
            this.currentProgramStatus = config.REPRO_FILE_DOWNLOADING;
            this.containerMode = config.CONTAINER_EXECUTING;
            this.execPhase = config.REPRO_FILE_DOWNLOADING;
            this.progressObjects = this.getProgressObjects(INIT);

            this.setupWatcher();
          },
          (result) => {
            // 不正応答コールバック
            const cause = result.causeType;
            const behaviorType = result.behaviorType;
            switch (cause) {
              // セッションが不正・ネットワークエラー・CCエラーならログアウト
              case handler.causeTypes.networkError:
              case handler.causeTypes.invalidSessionToken:
              case handler.causeTypes.clientConnectorNotAliveErrorError:
                alert.showWarning(this, null, result.message, () => {
                  if (behaviorType === handler.behaviorTypes.logout) {
                    this.logout(this);
                  }
                  else if (behaviorType === handler.behaviorTypes.resetSession) {
                    this.resetSession(this);
                  }
                });
                break;
              // リプロ開始前にメンテナンス待機中のエラー
              case handler.causeTypes.beforMaintenanceError:
                alert.showWarning(this, null, result.message, () => {
                  // システム画面へ遷移
                  if (this.vehicleInfo.vehicle.is_su_flow) {
                    this.$router.push({ name: 'suSystemTable' });
                  } else {
                    this.$router.push({ name: 'systemTable' });
                  }
                });
                break;
              default:
                // Move to system information screen if PFC json preparation failed in re-repro.
                if (this.reuseBinMot && result.consoleCode === Error.DB_REPRO_TARGET_UNIT_NOT_FOUND) {
                  this.moveToSystemTable(result.message);
                } else if (result.consoleCode === Error.DB_PFC_FILE_TARGET_ID_NOT_MATCH) {
                  const targetIds = this.idsInfo.newIds.join(', ');
                  const message = this.$t(api.getErrorMessageForCode(result.consoleCode), [targetIds, this.idsInfo.currentId]);
                  this.moveToSystemTable(message);
                } else if (result.consoleCode === Error.DB_PFC_FILE_ALREADY_REPROGRAM) {
                  // 本エラーメッセージは、リプログラミング失敗時のメッセージを使用するため action メッセージ対応を実施してメッセージを表示する
                  const details = this.$t('action.SEQUENCE_APP_ERR_WRONG_ID_NEW.cause');
                  const message = details.replace('$ERROR_MSG$', this.idsInfo.currentId);
                  this.moveToSystemTable(message);
                } else {
                  // その他のエラー
                  this.showUnexpectedErrorModal(result.message);
                }
            }
          }, null, false
        );
      } catch (error) {
        this.processing = false;
        this.showUnexpectedErrorModal(null);
      }
    },

    /* eslint-disable max-statements */
    /* eslint-disable complexity */
    // CC との接続状態を確認
    checkPairingStatus: async function () {
      let pairingStatus = null;
      this.executeRspMsgCheckTimeout -= (process.env.VUE_APP_RETRY_INTERVAL / 1000);
      // ステータスの問い合わせ
      const param = {
        this: this,
        netAppId: null
      };
      const pairingResponse = await this.getPairingStatus(param);
      // ペアリング情報を取得して接続もレスポンスも無効な場合はここで処理を終了する。
      if (!pairingResponse.isConnected && !pairingResponse.response) {
        return;
      }
      const isValid = handler.validate(handler.validateTypes.all, pairingResponse.response, this, null, () => { },
        // 不正応答コールバック
        (result) => {
          const cause = result.causeType;
          const behaviorType = result.behaviorType;
          // セッションが不正・ネットワークエラーならログアウト
          if (cause === handler.causeTypes.networkError ||
            cause === handler.causeTypes.invalidSessionToken) {
            this.clearWatcher();
            alert.showWarning(this, null, result.message, () => {
              if (behaviorType === handler.behaviorTypes.logout) {
                this.logout(this);
              }
              else if (behaviorType === handler.behaviorTypes.resetSession) {
                this.resetSession(this);
              }
            });
            return;
          }
          else if (cause === handler.causeTypes.clientConnectorNotAliveError) {
            // CC が起動していない
            this.showCCNotAliveModal();
          }
          else {
            // その他のエラー
            this.showUnexpectedErrorModal(result.message);
          }
          return;
        }, null, false
      );

      // ステータスの応答が不正なら後続処理を実施しない
      if (!isValid) return;

      // CC が非アクティブならリトライしない
      pairingStatus = pairingResponse.response.data;
      if (pairingStatus.connect_status === 'disconnected') {
        // CCがネットワーク切断のケースは別のエラーダイアログを表示する。
        this.showCCDisconnectedModal();
        return;
      }
      if (pairingStatus.connect_status !== 'active' ||
        pairingStatus.net_app_status === 'none' ||
        pairingStatus.net_app_status === 'pairing_failed' ||
        pairingStatus.user_id !== session.getUser()) {
        logWrapper.log('Pairing: connect_status ', pairingStatus.connect_status);
        logWrapper.log('Pairing: net_app_status ', pairingStatus.net_app_status);
        this.showCCNotAliveModal();
        return;
      } 

      // リプロは実行中に一定時間 CC からの応答がなければ再リプロ
      if (pairingStatus.repro_status === 'executing' &&
        !pairingStatus.repro_exec_rsp_received &&
        this.executeRspMsgCheckTimeout <= 0) {
        this.executeRspMsgCheckTimeout = Number(process.env.VUE_APP_EXECUTE_RSP_CHECK_INTERVAL);
        try {
          // if (this.reuseBinMot) await api.refreshToken();

          const loginToken = session.getLoginToken();
          const token = session.getToken();
          let restartResponse = null;
          if (this.isTestMode) {
            restartResponse = await api.postCall(config.REPRO, '/test/' + this.netAppId + '/startRepro', {
              is_req_resend: true, // 再実行
              id_token: (this.reuseBinMot) ? token.id_token : loginToken.id_token,
              unit_id: String(this.getUnitId())
            });
          } else {
            restartResponse = await api.postCall(config.REPRO, '/netApps/' + this.netAppId + '/startRepro', {
              is_req_resend: true, // 再実行
              is_bin_mot_reuse: this.reuseBinMot,
              id_token: (this.reuseBinMot) ? token.id_token : loginToken.id_token,
              pfc_id: this.unitInfo.fileId,
              unit_id: this.unitInfo.unitId,
              serial_number: this.serialNumber,
            });
          }
          handler.validate(handler.validateTypes.all, restartResponse, this, null, () => { },
            // 不正応答コールバック
            (result) => {
              const cause = result.causeType;
              const behaviorType = result.behaviorType;
              // セッションが不正・ネットワークエラーならログアウト
              if (cause === handler.causeTypes.networkError ||
                cause === handler.causeTypes.invalidSessionToken ||
                cause === handler.causeTypes.clientConnectorNotAliveErrorError) {
                this.clearWatcher();
                alert.showWarning(this, null, result.message, () => {
                  if (behaviorType === handler.behaviorTypes.logout) {
                    this.logout(this);
                  }
                  else if (behaviorType === handler.behaviorTypes.resetSession) {
                    this.resetSession(this);
                  }
                });
              }
              else {
                // Move to system information screen if PFC json preparation failed in re-repro.
                if (this.reuseBinMot && result.consoleCode === Error.DB_REPRO_TARGET_UNIT_NOT_FOUND) {
                  this.moveToSystemTable(result.message);
                  return;
                } else if (result.consoleCode === Error.DB_PFC_FILE_TARGET_ID_NOT_MATCH) {
                  const targetIds = this.idsInfo.newIds.join(', ');
                  const message = this.$t(api.getErrorMessageForCode(result.consoleCode), [targetIds, this.idsInfo.currentId]);
                  this.moveToSystemTable(message);
                  return;
                }
                // その他のエラー
                this.showUnexpectedErrorModal(result.message);
              }
              return;
            }, null, false
          );
        } catch (error) {
          // console.log('Unexpected error has occured @ExecuteRepro#checkPairingStatus: ', error);
        }
      }

      this.setProgress({
        systems: pairingStatus.systems,
        download: pairingStatus.download,
        preProgram: pairingStatus.pre_program,
        postProgram: pairingStatus.post_program
      });
      this.previousProgramStatus = this.currentProgramStatus;
      this.currentProgramStatus = pairingStatus.current_program_status;

      // 進捗が 100% 到達前に処理フェーズが変化した場合、前フェーズの進捗を 100% に設定する対応
      if (this.previousProgramStatus !== this.currentProgramStatus) {
        this.progressObjects = this.getProgressObjects(COMP);
        await util.sleep(1000);
      }

      if (pairingStatus.repro_status === 'executing' ||
        pairingStatus.repro_status === 'executing_ig_off' ||
        pairingStatus.repro_status === 'executing_ig_on') {
        if (pairingStatus.repro_status === 'executing_ig_off' && this.containerOrder === '') {
          this.containerOrder = 'OFF';
          this.containerMode = config.CONTAINER_CHECK_IG_OFF;
        }
        const currentPhase = this.getCurrentPhase();
        // ダイアログの切り替え
        if (this.fileType === config.PFC_FILE_TYPE_CCU && !this.isAlreadyShowCCUContainer) {
          if (pairingStatus.repro_status !== 'executing_ig_off' && pairingStatus.repro_status !== 'executing_ig_on') {
            // CCU用ダイアログへ切り替え
            if (currentPhase !== config.REPRO_FILE_DOWNLOADING && currentPhase !== config.REPRO_PRE_PROGRAMMING) {
              this.showCcuReprogrammingContainer();
              return;
            }
          }
        }
        this.execPhase = currentPhase;
        // 処理フェーズが変化した直後の進捗を 0% で初期化する対応
        if (this.previousProgramStatus !== this.currentProgramStatus) {
          this.progressObjects = this.getProgressObjects(INIT);
          await util.sleep(1000);
        }
        // 表示する進捗データを現在のものに設定
        this.progressObjects = this.getProgressObjects(CURRENT);
        return;
      }

      // ステータスが失敗になった場合
      if (pairingStatus.repro_status === 'failure') {
        // エラーコード・エラーメッセージをセット
        this.setResultInformation(pairingStatus);
        // 結果画面でサインアウト・再サインインされたときでもエラー内容を保持
        // 1 ECU Inquiry 実行により、pairing テーブルから前回のリプロ失敗情報が削除されるため本対応が必要
        this.setUserCache({ key: config.USER_CACHE_REPRO_FAILUER_INFO, value: pairingStatus });

        // 接続状況監視を停止
        this.clearWatcher();

        // リプロ後処理でのエラーかどうかで結果画面を切り替え
        let result = config.STATUS_RPR_EXECUTING_ERROR;
        if (this.hasPostReprogramError(pairingStatus)) { result = config.STATUS_RPR_FINISHED_ERROR; }
        this.moveResult(result);
        return;
      }

      if (pairingStatus.repro_status === 'success') {
        // 後処理フェーズの進捗を 100% に設定
        this.progressObjects = this.getProgressObjects(CURRENT);
        await util.sleep(1000);

        this.setResultInformation(pairingStatus);
        this.clearWatcher();
        this.moveResult(config.STATUS_RPR_FINISHED_SUCCESS);
      }
    },
    /* eslint-enable max-statements */

    // 80 秒待機または700秒待機コンテナを表示
    startCountDown: function () {
      let totalCount = 0;
      if (this.isLongInterval) {
        totalCount = Number(process.env.VUE_APP_IG_ON_LONG_INTERVAL);
      } else {
        totalCount = Number(process.env.VUE_APP_IG_ON_INTERVAL);
      }
      this.countDownTimer = totalCount;
      this.igStateTimer = this.$setInterval(() => {
        // 設定されたIG_ON のカウント数を1秒毎に1減らす
        --totalCount;
        if (totalCount === 0) {
          this.containerOrder = 'ON';
          this.$clearInterval(this.igStateTimer);
          this.igStateTimer = null;
          this.containerMode = config.CONTAINER_CHECK_IG_ON;
          return;
        }
        this.countDownTimer = totalCount;
      }, 1000);
    },

    // IG-OFF チェックを確認
    confirmIgOff: async function () {
      this.processing = true;
      const igOffResponse = await api.postCall(config.REPRO, '/igOff/' + this.netAppId);
      this.processing = false;
      handler.validate(handler.validateTypes.all, igOffResponse, this, null,
        () => {
          // 正常コールバック
          this.containerOrder = 'OFF_PUSHED';
          if (this.isLongInterval) {
            this.containerMode = config.CONTAINER_IS_LONG_INTERVAL;
          } else {
            this.containerMode = config.CONTAINER_IG_COUNT_DOWN;
          }
          this.startCountDown();
        },
        (result) => {
          // 不正応答コールバック
          const cause = result.causeType;
          const behaviorType = result.behaviorType;
          // セッションが不正・ネットワークエラーならログアウト
          if (cause === handler.causeTypes.networkError ||
            cause === handler.causeTypes.invalidSessionToken) {
            this.clearWatcher();
            alert.showWarning(this, null, result.message, () => {
              if (behaviorType === handler.behaviorTypes.logout) {
                this.logout(this);
              }
              else if (behaviorType === handler.behaviorTypes.resetSession) {
                this.resetSession(this);
              }
            });
          }
          else if (cause === handler.causeTypes.clientConnectorNotAliveError) {
            // CC が起動していない or IG-OFF 失敗は強制サインアウト
            this.showCCNotAliveModal();
          }
          else {
            // その他のエラー
            this.showUnexpectedErrorModal(result.message);
          }
          return;
        }, null, false
      );
    },

    // IG-ON チェックを確認
    confirmIgOn: async function () {
      // IG-ON
      this.containerMode = config.CONTAINER_EXECUTING;
      this.execPhase = this.getCurrentPhase();

      // igOnのAPIを実施する前はステータス監視タイマーを一時停止
      this.$pauseInterval(this.watcher);
      const igOnResponse = await api.postCall(config.REPRO, '/igOn/' + this.netAppId);
      handler.validate(handler.validateTypes.all, igOnResponse, this, null,
        () => {
          // 正常コールバック
          // igOnのAPIを成功後はステータス監視タイマーを再開
          this.$resumeInterval(this.watcher);
        },
        (result) => {
          // 不正応答コールバック
          const cause = result.causeType;
          const behaviorType = result.behaviorType;
          // セッションが不正・ネットワークエラーならログアウト
          if (cause === handler.causeTypes.networkError ||
            cause === handler.causeTypes.invalidSessionToken) {
            this.clearWatcher();
            alert.showWarning(this, null, result.message, () => {
              if (behaviorType === handler.behaviorTypes.logout) {
                this.logout(this);
              }
              else if (behaviorType === handler.behaviorTypes.resetSession) {
                this.resetSession(this);
              }
            });
          }
          else if (cause === handler.causeTypes.clientConnectorNotAliveError) {
            // CC が起動していない or IG-ON 失敗は強制サインアウト
            this.showCCNotAliveModal();
          }
          else {
            // その他のエラー
            this.showUnexpectedErrorModal(result.message);
          }
          return;
        }, null, false
      );
    },

    // CC が終了していた時のダイアログを表示
    showCCNotAliveModal: function () {
      this.clearWatcher();
      alert.showError(this, null, this.$t(api.getErrorMessageForCode(Error.CLOUD_CONNECTOR_NOT_ACTIVE)),
        () => { this.logout(this); }
      );
    },

    // CC がリプロ中にネットワーク切断されていた時のダイアログを表示
    showCCDisconnectedModal: function () {
      this.clearWatcher();
      alert.showError(this, null, this.$t(api.getErrorMessageForCode(Error.CLOUD_CONNECTOR_DISCONNECT_IN_REPROGRAMMING)),
        () => { this.logout(this); }
      );
    },

    // 予期しないエラー発生時のダイアログを表示
    showUnexpectedErrorModal: function (msg) {
      this.clearWatcher();
      alert.showError(this, null, msg, () => { this.logout(this); });
    },

    // Move to system table screen when error occurs
    moveToSystemTable: function (msg) {
      this.clearWatcher();
      alert.showWarning(this, null, msg, async () => {
        // 正常に取得した情報を内部でキャッシュ
        this.cacheVehicleInfo(this.vehicleInfo);
        // Refresh published and unpublished unit list
        await this.$emit('refreshAllReproSystems');
        // システム画面へ遷移
        if (this.vehicleInfo.vehicle.is_su_flow) {
          this.$router.push({ name: 'suSystemTable', params: { previousDisplayName: 'execute' } });
        } else {
          this.$router.push({ name: 'systemTable', params: { previousDisplayName: 'execute' } });
        }
      });
    },

    // 80 秒待機コンテナのヘッダ文言リソースを取得
    getCountDownHeader: function () {
      // isLongIntervalがtrueの場合、ヘッダーを対応したものにする
      if (this.isLongInterval) {
        return this.$t('message.header_repro_ig_long_count_down');
      } else {
        return this.$t('message.header_repro_ig_count_down');
      }
    },

    // タイマーのセット
    setupWatcher: function () {
      this.isAlreadyShowCCUContainer = false;
      this.executeRspMsgCheckTimeout = Number(process.env.VUE_APP_EXECUTE_RSP_CHECK_INTERVAL);
      this.watcher = this.$startInterval(async () => {
        this.checkPairingStatus();
      }, process.env.VUE_APP_RETRY_INTERVAL, "ExecuteRepro");
    },

    // タイマーのクリア
    clearWatcher: function () {
      if (this.watcher) {
        this.$stopInterval(this.watcher);
        this.watcher = null;
      }
      if (this.igStateTimer) {
        this.$clearInterval(this.igStateTimer);
        this.igStateTimer = null;
      }

      this.$stopInterval(this.vehicleWatcher);

      this.containerOrder = '';
      this.executeRspMsgCheckTimeout = Number(process.env.VUE_APP_EXECUTE_RSP_CHECK_INTERVAL);
    },

    // リプロ後処理でエラーが発生しているか判定
    hasPostReprogramError: function (status) {
      if (status.current_program_status === config.REPRO_POST_PROGRAMMING) {
        return true;
      }
      return false;
    },

    // 結果詳細を設定する
    setResultInformation: function (status) {
      this.setResultStatus(status);
    },

    // Get unit id for test mode
    getUnitId: function () {
      if (!this.unitInfo.unitId) {
        return this.testReproUnitId;
      }

      let filteredList = publishedUnitListTestMode.filter(item => item.id === this.unitInfo.unitId);
      if (filteredList.length === 0) {
        return this.unitInfo.unitId;
      }
      const systemName = filteredList[0].main_system_name.toUpperCase();
      filteredList = this.targetUnitList.filter(unit => unit.name.toUpperCase() === systemName);
      if (filteredList.length === 0) {
        return this.unitInfo.unitId;
      }
      return filteredList[0].id;
    },

    // Convert the file download progress
    convertFileDownloadProgress: function () {
      const fileSizeUnitList = ['B', 'KB', 'MB', 'GB', 'TB'];
      let convertedDownloadBytes = '0';
      let convertedTotalBytes = '0';
      let unit = fileSizeUnitList[2];
      if (this.download.total_bytes > 0) {
        const index = Math.floor(Math.log(this.download.total_bytes) / Math.log(1024));
        unit = fileSizeUnitList[index];
        convertedTotalBytes = (this.download.total_bytes / Math.pow(1024, index)).toFixed(1);
        if (this.download.downloaded_bytes > 0) {
          convertedDownloadBytes = (this.download.downloaded_bytes / Math.pow(1024, index)).toFixed(1);
        }
      }
      if (convertedDownloadBytes === '0' && convertedTotalBytes === '0') {
        return '';
      }
      let remainingTimeDisplay = '';
      const conversionInfo = reprogram.calculateFileDownloadTime(this.download.remaining_download_time);
      if (conversionInfo) {
        remainingTimeDisplay = `${this.$t('message.label_repro_execute_dl_remaining_time')}`;
        if (conversionInfo.minutes > 0) {
          remainingTimeDisplay = `${remainingTimeDisplay} ${conversionInfo.minutes} ${this.$t('message.label_repro_execute_dl_unit_min')}`;
        }
        remainingTimeDisplay = `${remainingTimeDisplay} ${conversionInfo.seconds} ${this.$t('message.label_repro_execute_dl_unit_sec')}`;
      }
      return `(${convertedDownloadBytes}/${convertedTotalBytes} ${unit}) ${remainingTimeDisplay}`;
    },

    getRemainingTimeText: function (time) {
      if (!time) {
        return '';
      }

      const remainingTimeInMins = Math.ceil(time / 60000);
      if (this.language === 'ja') {
        return `${this.$t('message.label_repro_execute_remaining_text')} ${remainingTimeInMins} ${this.$t('message.label_repro_execute_dl_unit_min')}`;
      } else {
        return `${remainingTimeInMins} ${this.$t('message.label_repro_execute_dl_unit_min')} ${this.$t('message.label_repro_execute_remaining_text')}`;
      }
    },

    getProgressObjects: function (type) {
      const progressInfo = {
        system_name: "",
        progress: 0,
        remaining_repro_time: '',
        repro_exec_status: ''
      };

      // 実行中フェーズの進捗初期化・進捗完了は実行しない
      if (this.currentProgramStatus === config.REPRO_PROGRAMMING && type === INIT) return progressInfo;
      if (this.previousProgramStatus === config.REPRO_PROGRAMMING && type === COMP) return progressInfo;
      let progress = 0;
      let reproExecStatus = '';
      const status = type === COMP ? this.previousProgramStatus : this.currentProgramStatus;
      switch (status) {
        case config.REPRO_FILE_DOWNLOADING:
          if (type === COMP) progress = 100;
          else if (type === INIT) progress = 0;
          else progress = this.download.progress;
          reproExecStatus = this.$t('message.state_reprogram_file_downloading');
          reproExecStatus = `${reproExecStatus} ${this.convertFileDownloadProgress()}`;
          break;
        case config.REPRO_PRE_PROGRAMMING:
          if (type === COMP) progress = 100;
          else if (type === INIT) progress = 0;
          else progress = this.preProgram.progress;
          reproExecStatus = this.$t('message.status_repro_execute_pre_program');
          break;
        case config.REPRO_POST_PROGRAMMING:
          if (type === COMP) progress = 100;
          else if (type === INIT) progress = 0;
          else progress = this.postProgram.progress;
          reproExecStatus = this.$t('message.status_repro_execute_post_program');
          break;
        default: break;
      }

      progressInfo.system_name = this.systems.map(system => system.system_name).join('/');
      progressInfo.progress = progress;
      progressInfo.repro_exec_status = reproExecStatus;
      const remainingTime = this.systems.reduce((time, system) => time + (system.remaining_repro_time || 0), 0);
      if (remainingTime > 0) {
        progressInfo.remaining_repro_time = this.getRemainingTimeText(remainingTime);
      }
      return progressInfo;
    },

    // Pre/Post Programming progress status
    isPreAndPostProgramming: function () {
      let result = false;
      switch (this.getCurrentPhase()) {
        case config.REPRO_FILE_DOWNLOADING:
        case config.REPRO_PRE_PROGRAMMING:
        case config.REPRO_POST_PROGRAMMING:
          result = true; break;
      }
      return result;
    },

    // 現在のリプロ処理フェーズを取得
    getCurrentPhase: function () {
      return this.containerMode === config.CONTAINER_EXECUTING ? this.currentProgramStatus : '';
    },

    // エラーコンテナの内容を設定する
    setErrorContent: function (err) {
      const status = {
        errorCode: err.validatorResult.err_code,
        errorMessage: err.message,
        originalError: true
      };
      this.setResultStatus(status);
      this.moveResult(config.STATUS_RPR_EXECUTING_ERROR, true);
    },

    // 個別 Inquiry を実行してから、結果画面に遷移する
    moveResult: function (rprStatus, noNeedToInquiry = false) {
      if (!noNeedToInquiry) {
        // err_code_list をキャッシュも含めて初期化
        this.setWarningQueue('');
        this.setStackedWarningQueue('');
        this.canExitAllWatcher = false;
        this.isReproCompleted = true;
        const param = {
          netAppId: this.netAppId,
          endPoint: '',
          this: this,
          unitId: this.unitInfo.unitId,
          options: { exitOpt: true },  // rxswin/battery エラーがあればポーリング中止
          type: config.REPRO
        };
        // 個別 Inquiry の状態を監視する watcher を登録
        const inquiryWatcher = this.$watch('vehicleStatus', async (val) => {
          if (this.canExitAllWatcher) {
            // RxSWIN 更新でのエラーを検知したら監視状態を検証しない
            inquiryWatcher();
            return;
          }
          if (val === config.POLLING_FAILURE) {
            // リプロ自体は完了しているので、個別 Inquiry でエラーが発生しても
            // サインアウトせず、結果画面に遷移してリプロ結果を表示する
            this.processing = false;
            inquiryWatcher();
            const error = this.vehicleError;
            if (error.validatorResult) {
              // rxswin 更新失敗(2041) の場合はシステム一覧を更新
              if (error.validatorResult.consoleCode === Error.CLOUD_CONNECTOR_UPDATE_RXSWIN_FAILED) {
                await this.$emit('refreshAllReproSystems');
              }
              // ネットワーク接続エラーとセッション情報エラーは警告メッセージ
              if (this.isShowWarning(error)) {
                alert.showWarning(this, null, error.message, () => {
                  if (error.behaviorType === handler.behaviorTypes.resetSession) {
                    this.resetSession(this);
                  }
                  this.pushToResult(rprStatus);
                });
                return;
              }
            }
            // その他のエラー
            alert.showError(this, null, error.message, () => { this.pushToResult(rprStatus); });
          }
          else if (val === config.POLLING_COMPLETED) {
            inquiryWatcher();
            // 正常に取得した情報を内部でキャッシュ
            this.cacheVehicleInfo(this.vehicleInfo);
            // Refresh published and unpublished unit list
            const systemMode = vehicle.getVehicleSystemMode(this.isTestMode, this.isTechnicalHq);
            const param = {
              netAppId: this.netAppId,
              obj: this,
              isBothTable: authority.hasUnpublishedPermission(this.authGroup),
              mode: systemMode
            };
            await this.setNativeSystemTable(param);
            this.processing = false;
            this.pushToResult(rprStatus);
          }
        });
        // RxSWIN 更新の状態を監視する watcher を登録
        const killWarningWatcher = this.$watch('warnings', async (val) => {
          // リプロ実行中は err_code_list を監視しない
          if (!this.isReproCompleted) return;
          modalQueue = val ? val.split(',') : [];
          // RxSWIN 更新異常を検知したら、個別 Inquiry 監視コールバックを
          // 無効化して検知した異常の種類・数に応じた Modal Queue 表示開始
          if (modalQueue.length > 0 && !this.canExitAllWatcher) {
            this.canExitAllWatcher = true;
            this.startModalQueue();
            this.processing = false;
            killWarningWatcher();
          }
        });
        this.processing = true;
        this.innerProgressIcon = false;
        this.updateVehicleInfo(param);
      } else {
        this.pushToResult(rprStatus);
      }
    },

    pushToResult: function (rprStatus) {
      if (this.$router.currentRoute.value.name !== 'result') {
        this.$router.push({ name: 'result', params: { result: rprStatus } });
      }
    },

    isShowWarning: function (error) {
      return error.validatorResult.causeType === handler.causeTypes.networkError || error.validatorResult.causeType === handler.causeTypes.invalidSessionToken;
    },

    startModalQueue: function () {
      if (modalQueue.length > 0) {
        this.modalQueueMsg = { lead: '', tail: '' };
        const msgConfig = resource.getRxswinErrorMessageSet(modalQueue[0]);
        this.modalQueueMsg.lead = msgConfig.lead ? this.$t(msgConfig.lead) : '';
        this.modalQueueMsg.tail = msgConfig.tail ? this.$t(msgConfig.tail) : '';
        this.showModalQueueWarning = true;
      } else {
        this.modalQueueMsg = { lead: '', tail: '' };
        this.showModalQueueWarning = false;
        this.$router.push({ name: 'home', params: { isAuto: false, isConfirmSkip: true } });
      }
    },

    closeModalQueue: function () {
      this.showModalQueueWarning = false;
      modalQueue.shift();
    },

    callModalHiddenEvent: function () {
      this.startModalQueue();
    },

    // CCU リプログラミング開始
    showCcuReprogrammingContainer: function () {
      // CCU リプロコンテナの表示巻き戻り防止フラグの設定
      this.isAlreadyShowCCUContainer = true;
      // CCU リプロ中はステータス監視タイマーを一時停止
      this.$pauseInterval(this.watcher);
      this.execPhase = '';
      this.containerMode = config.CONTAINER_CCU_REPROGRAMMING;
    },

    // CCU リプログラミング完了確認
    confirmCompleteCcuReprogramming: function () {
      this.containerMode = config.CONTAINER_CONFIRM_COMPLETE_CCU_REPROGRAMMING;
    },

    // CCU リプログラミング完了後処理
    completeCcuReprogramming: function () {
      // ステータス監視タイマーを再開
      this.$resumeInterval(this.watcher);
      this.containerMode = config.CONTAINER_CHECK_IG_OFF;
    }
  },

  async created() {
    let currentStatus = null;
    if (this.response) {
      currentStatus = JSON.parse(this.response);
    }

    if (this.isTestMode && this.systems) {
      const filteredList = publishedUnitListTestMode.filter(item => {
        for (let index = 0; index < item.system_names.length; ++index) {
          for (let sysIndex = 0; sysIndex < this.systems.length; ++sysIndex) {
            if ((item.system_names[index].system_name === this.systems[sysIndex].system_name) ||
              item.main_system_name === this.systems[sysIndex].system_name) {
              return true;
            }
          }
        }
        return false;
      });
      if (filteredList.length > 0) {
        this.testReproUnitId = filteredList[0].id;
      }
    }

    // リプロ開始
    if (!currentStatus) {
      // PFCファイルのタイプ情報を保持
      this.fileType = this.ccuReproInfo ? this.ccuReproInfo.fileType : '';
      // アイサイトの再リプロ時のみ車両情報を再取得
      if (this.reuseBinMot && !this.isTestMode) {
        const data = {
          netAppId: this.netAppId,
          endPoint: '',
          this: this,
          unitId: this.unitInfo.unitId,
          type: config.REPRO
        };
        // アイサイト再リプロの車両情報再取得を監視するインスタンス
        const eyeRereproWatcher = this.$watch('vehicleStatus', (val) => {
          // 失敗
          if (val === config.POLLING_FAILURE) {
            eyeRereproWatcher();
            this.processing = false;
            // 車両情報取得のwait画面を終了
            const error = this.vehicleError;
            // APIバリデータの戻りを確認
            if (error.validatorResult) {
              // ネットワーク接続エラーとセッション情報エラーは警告メッセージで表示する
              if (this.isShowWarning(error)) {
                alert.showWarning(this, null, error.message, () => {
                  if (error.behaviorType === handler.behaviorTypes.logout) {
                    this.logout(this);
                  }
                  else if (error.behaviorType === handler.behaviorTypes.resetSession) {
                    this.resetSession(this);
                  }
                });
                return;
              }
              this.setErrorContent(error);
            }
            else {
              this.setErrorContent(error);
            }
          }
          // 成功
          else if (val === config.POLLING_COMPLETED) {
            eyeRereproWatcher();
            this.processing = false;
            // リプロ実行
            this.execute();
          }
        });
        this.processing = true;
        this.updateVehicleInfo(data);
      } else {
        this.execute();
      }
    }
    // リプロ復帰
    else {
      // リプロ実行中にサインアウト・再サインイン
      // リプロ状況を設定
      const is_bin_mot_reuse = typeof currentStatus.is_bin_mot_reuse === 'undefined' ? false : currentStatus.is_bin_mot_reuse;
      this.setPFCReuseStatus(is_bin_mot_reuse);
      this.setProgress({
        systems: currentStatus.systems,
        download: currentStatus.download,
        preProgram: currentStatus.pre_program,
        postProgram: currentStatus.post_program
      });
      this.isAlreadyShowCCUContainer = false;
      // リプロ結果画面に表示するためののシステム情報を再取得
      // 1 ECU Inquiry 後では取得できないので、このタイミングで要取得
      this.processing = true;
      const endpoint = this.netAppId + '/' + this.unitInfo.unitId + '/' + 'reproTargets' + '/' + this.unitInfo.fileId;
      const res = await api.getCall(config.REPRO, '/netApps/' + endpoint);
      this.processing = false;
      handler.validate(handler.validateTypes.all, res, this, null, () => {
        this.setUnitDetailInfo(res.data.units);
        // 待機処理が長いシステムか判定
        this.setIsLongInterval(res.data.is_long_interval);
        // PFCファイルのタイプ情報を保持
        const units = res.data.units || [];
        this.fileType = units.length > 0 ? units[0].file_type : '';
      }, () => {
        // 情報取得で失敗してもエラーとはしない。
        this.setUnitDetailInfo(null);
      }, null, false);

      // リプロ実行状況に応じて表示コンテナを制御
      const status = currentStatus.repro_status;

      this.previousProgramStatus = currentStatus.current_program_status;
      this.currentProgramStatus = currentStatus.current_program_status;

      if (status === 'executing' || status === 'executing_ig_on') {
        // 進捗ゲージを表示
        this.containerMode = config.CONTAINER_EXECUTING;
      } else if (status === 'executing_ig_off') {
        // IG-OFF 操作コンテナ表示
        this.containerMode = config.CONTAINER_CHECK_IG_OFF;
      } else if (status === 'failure') {
        // リプロ失敗なら vuex にエラー詳細を設定して結果画面に遷移
        this.setResultInformation(currentStatus);
        if (this.hasPostReprogramError(currentStatus))
          this.moveResult(config.STATUS_RPR_FINISHED_ERROR);
        else
          this.moveResult(config.STATUS_RPR_EXECUTING_ERROR);
        return;
      }

      // リプロステータス監視を開始
      this.setupWatcher();
    }
  },

  mounted() {
    // noop
  },

  beforeUnmount() {
    this.clearWatcher();
    modalQueue = [];
  }
};